import { type FormEvent, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { defineStepper } from "@stepperize/react";
import { FormProvider, type FieldPath, type Resolver, useForm } from "react-hook-form";
import { Info } from "lucide-react";
import Module from "../../../../components/Module";
import Loading from "../../../../components/Loading";
import { displayErrorMessage } from "../../../../utils";
import { Grid, Row, Col } from "../../../../components/Grid";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { WFPButton } from "../../../../components/ui/button";
import { useTranslation } from "react-i18next";
import {
  AggregatorDetailsProfile,
  type AggregatorDetailsProfileFormValues,
  createAggregatorDetailsProfileSchema,
} from "./Components/AggregatorDetailsProfile";
import { AggregatorAdministratorsTable } from "./Components/AggregatorAdministratorsTable";
import { AggregatorRegistrationStepNavigation } from "./Components/AggregatorRegistrationStepNavigation";
import {
  AddAdministratorForm,
  type AdministratorFormValues,
} from "./Components/AdministratorModal";
import { DeleteAdministratorDialog } from "./Components/DeleteAdministratorDialog";
import { EnableAdministratorDialog } from "./Components/EnableAdministratorDialog";
import {
  STEP_IDS,
  type AggregatorDetailsFormProps,
  type EntityId,
  type StepId,
  type TranslationFunction,
} from "./Components/types";

export type { AggregatorDetailsFormProps } from "./Components/types";

type AggregatorDetailsFormContentProps = AggregatorDetailsFormProps & {
  t: TranslationFunction;
};

const aggregatorRegistrationStepper = defineStepper([
  {
    id: STEP_IDS.DETAILS,
    titleKey: "aggregatorDetails.aggregatorDetails",
  },
  {
    id: STEP_IDS.ADMINISTRATORS,
    titleKey: "aggregatorDetails.addAdministrators",
  },
] as const);

const DETAILS_STEP_FIELDS: FieldPath<AggregatorDetailsProfileFormValues>[] = [
  "name",
  "org_type",
  "language",
  "location",
];

const getInitialStepId = (initialStep: number | undefined, id: EntityId): StepId =>
  initialStep === 1 && id ? STEP_IDS.ADMINISTRATORS : STEP_IDS.DETAILS;

const AggregatorDetailsFormContent = ({
  id,
  onClickValidateAndClose,
  onClickValidateAndContinue,
  aggregatorAdministrators = [],
  onClickEditAdministrator,
  onClickDeleteAdministrator,
  onClickEnableAdministrator,
  isDeleteAdministratorModalOpen,
  onClickConfirmDeleteAdministrator,
  onClickCancelDeleteAdministrator,
  currentToDeleteAdministrator = null,
  isFetchingDeleteAdministrator,
  errorMessageDeleteAdministrator,
  organizationTypes = [],
  administrativeAreas = [],
  initialStep,
  t,
  initialValues = {},
  isSavingProfile = false,
  isFetchingAggregatorAdministrators = false,
  initialFormValuesAdministrator = {},
  isFetchingAdministrator = false,
  errorMessageAdministrator = "",
  fieldErrorsAdministrator = {},
  onClickValidateAdministratorModal,
  isOnEditAdministrator = false,
  onClickCloseAdministrator,
  isEnableAdministratorModalOpen = false,
  onClickConfirmEnableAdministrator,
  onClickCancelEnableAdministrator,
  currentToEnableAdministrator = null,
  isFetchingEnableAdministrator = false,
  errorMessageEnableAdministrator = "",
}: AggregatorDetailsFormContentProps) => {
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [aggregatorAdminWarning, setAggregatorAdminWarning] = useState("");
  const hasAdministrators = aggregatorAdministrators && aggregatorAdministrators.length > 0;
  const completedSteps = useMemo(
    () => [
      ...(id ? [STEP_IDS.DETAILS] : []),
      ...(hasAdministrators ? [STEP_IDS.ADMINISTRATORS] : []),
    ],
    [hasAdministrators, id]
  );
  const stepperOptions = useMemo(
    () => ({
      completed: completedSteps,
      defaultStep: getInitialStepId(initialStep, id),
      linear: true,
    }),
    [completedSteps, id, initialStep]
  );
  const stepper = aggregatorRegistrationStepper.useStepper(stepperOptions);
  const aggregatorDetailsProfileSchema = useMemo(
    () => createAggregatorDetailsProfileSchema(t("traderDetails.form.mandatoryFieldBlankError")),
    [t]
  );
  const methods = useForm<AggregatorDetailsProfileFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(
      aggregatorDetailsProfileSchema
    ) as Resolver<AggregatorDetailsProfileFormValues>,
  });
  const {
    formState: { isSubmitting },
    getValues,
    handleSubmit,
    watch,
    trigger,
  } = methods;

  const locationValue = watch("location");
  const nameValue = watch("name");
  const orgTypeValue = watch("org_type");
  const languageValue = watch("language");
  const isAdministratorsStepActive = stepper.is(STEP_IDS.ADMINISTRATORS);
  const goToStep = stepper.goTo;
  const isStepperBusy = isSavingProfile || isSubmitting || stepper.isPending;
  const isProfileStepSaving = stepper.is(STEP_IDS.DETAILS) && (isSavingProfile || isSubmitting);

  useEffect(() => {
    if (showFieldErrors) {
      trigger(DETAILS_STEP_FIELDS);
    }
  }, [languageValue, locationValue, nameValue, orgTypeValue, showFieldErrors, trigger]);

  useEffect(() => {
    if (initialStep === 1 && id && !isAdministratorsStepActive) {
      goToStep(STEP_IDS.ADMINISTRATORS);
    }
  }, [goToStep, id, initialStep, isAdministratorsStepActive]);

  const nextButtonLabel = () =>
    stepper.is(STEP_IDS.ADMINISTRATORS) ? t("common.finish") : t("common.saveContinue");

  const deleteAdministratorMessage =
    (currentToDeleteAdministrator &&
      currentToDeleteAdministrator.person &&
      t("aggregatorDetails.form.deleteAdministratorMessage") +
        " " +
        currentToDeleteAdministrator.person.full_name +
        "?") ||
    "";

  const enableAdministratorMessage =
    (currentToEnableAdministrator &&
      currentToEnableAdministrator.person &&
      t("aggregatorDetails.form.enableAdministratorMessage") +
        " " +
        currentToEnableAdministrator.person.full_name +
        "?") ||
    "";

  const submitCurrentStep = handleSubmit(async formValues => {
    if (stepper.is(STEP_IDS.DETAILS)) {
      return onClickValidateAndContinue(formValues);
    }

    return onClickValidateAndClose(formValues);
  });

  const validateDetailsStep = async () => {
    setShowFieldErrors(true);
    return trigger(DETAILS_STEP_FIELDS);
  };

  const handleStepSelection = async (stepId: StepId) => {
    if (isStepperBusy) {
      return;
    }

    if (stepper.id === stepId) {
      return;
    }

    if (stepId === STEP_IDS.ADMINISTRATORS && !id) {
      return;
    }

    if (stepper.is(STEP_IDS.DETAILS) && stepId === STEP_IDS.ADMINISTRATORS) {
      const isDetailsStepValid = await validateDetailsStep();

      if (!isDetailsStepValid) {
        return;
      }
    }

    await stepper.goTo(stepId);
  };

  const next = async () => {
    if (isStepperBusy) {
      return;
    }

    if (stepper.is(STEP_IDS.DETAILS)) {
      const isDetailsStepValid = await validateDetailsStep();

      if (!isDetailsStepValid) {
        return;
      }

      await submitCurrentStep();
      return;
    }

    if (hasAdministrators) {
      await submitCurrentStep();
      return;
    }

    setAggregatorAdminWarning(t("aggregatorDetails.form.aggregatorAdminWarning"));
  };

  const validateAdministrator = (values: AdministratorFormValues) => {
    void onClickValidateAdministratorModal(values);
  };

  const getStepHeading = () => t(stepper.current.titleKey);

  const isStepDisabled = (stepId: StepId) => stepId === STEP_IDS.ADMINISTRATORS && !id;

  return (
    <>
      <Grid>
        <Row>
          <Col sm={2}>
            <AggregatorRegistrationStepNavigation
              steps={stepper.steps}
              activeStepId={stepper.id}
              isBusy={isStepperBusy}
              label={t("aggregatorDetails.pageTitle")}
              isStepComplete={stepId => stepper.isComplete(stepId)}
              isStepDisabled={isStepDisabled}
              onStepSelect={handleStepSelection}
              t={t}
            />
          </Col>
          <Col sm={8}>
            <Module
              title={getStepHeading()}
              footer={
                <div className="flex w-full justify-end">
                  <WFPButton type="button" disabled={isStepperBusy} onClick={next}>
                    {nextButtonLabel()}
                  </WFPButton>
                </div>
              }
            >
              <FormProvider {...methods}>
                <form
                  onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    next();
                  }}
                >
                  {stepper.is(STEP_IDS.DETAILS) && (
                    <AggregatorDetailsProfile
                      methods={methods}
                      showFieldErrors={showFieldErrors}
                      isSaving={isProfileStepSaving}
                      organizationTypes={organizationTypes}
                      administrativeAreas={administrativeAreas}
                    />
                  )}
                </form>
              </FormProvider>
              {stepper.is(STEP_IDS.ADMINISTRATORS) && (
                <Grid>
                  <Row>
                    <Col sm={12}>
                      <Alert variant="info" className="mb-3">
                        <Info />
                        <AlertDescription className="whitespace-pre-line">
                          {t("aggregatorDetails.form.adminInstructionText")}
                        </AlertDescription>
                      </Alert>
                    </Col>
                  </Row>
                  <AddAdministratorForm
                    id={id ?? undefined}
                    aggregator={getValues()}
                    initialValues={initialFormValuesAdministrator}
                    isFetching={isFetchingAdministrator}
                    errorMessage={errorMessageAdministrator}
                    fieldErrors={fieldErrorsAdministrator}
                    onClickValidate={validateAdministrator}
                    isOnEdit={isOnEditAdministrator}
                    onClickClose={onClickCloseAdministrator}
                  />
                  <Row>
                    <Col sm={12}>
                      <AggregatorAdministratorsTable
                        administrators={aggregatorAdministrators}
                        isLoading={isFetchingAggregatorAdministrators}
                        onClickDeleteAdministrator={onClickDeleteAdministrator}
                        onClickEditAdministrator={onClickEditAdministrator}
                        onClickEnableAdministrator={onClickEnableAdministrator}
                        t={t}
                        warningMessage={aggregatorAdminWarning}
                      />
                    </Col>
                  </Row>
                </Grid>
              )}
            </Module>
          </Col>
        </Row>
      </Grid>
      <DeleteAdministratorDialog
        isOpen={isDeleteAdministratorModalOpen}
        onValidate={onClickConfirmDeleteAdministrator}
        onCancel={onClickCancelDeleteAdministrator}
        errorMessage={errorMessageDeleteAdministrator}
        isLoading={isFetchingDeleteAdministrator}
        title={t("aggregatorDetails.form.deleteConfirmation")}
        text={deleteAdministratorMessage}
      />
      <EnableAdministratorDialog
        isOpen={isEnableAdministratorModalOpen}
        onValidate={onClickConfirmEnableAdministrator}
        onCancel={onClickCancelEnableAdministrator}
        errorMessage={errorMessageEnableAdministrator}
        isLoading={isFetchingEnableAdministrator}
        title={t("aggregatorDetails.form.enableConfirmation")}
        text={enableAdministratorMessage}
      />
    </>
  );
};

// The details of an Aggregator Organization
export function Form(props: AggregatorDetailsFormProps) {
  const { isFetching, errorMessage } = props;
  const { t } = useTranslation();
  const translate: TranslationFunction = (key, options) => t(key, options);

  if (isFetching) {
    return <Loading isVisible />;
  }

  if (errorMessage) {
    return <>{displayErrorMessage(errorMessage)}</>;
  }

  return (
    <AggregatorDetailsFormContent {...props} errorMessage={errorMessage ?? ""} t={translate} />
  );
}

export default Form;
